import {useMemo} from 'react'
import {useIntl} from 'react-intl'
import useCockpitGalleryImages from '../../pages/product-detail/hooks/use-cockpit-gallery-images'
import useMainSetProductImages from '../../pages/product-detail/hooks/use-main-set-product-images'
import {
    filterSetCombinationImages,
    getProductSetSelectionImageName
} from '../../utils/image-groups-utils'
import {MAIN_SET_PRODUCT_IMAGES_PRECEDING_COUNT} from '../../constants'

const isBrightcoveVideo = (item) => item.type === 'brightcove'

/**
 * Index the main set product images are injected at - the index of the image following the first
 * `MAIN_SET_PRODUCT_IMAGES_PRECEDING_COUNT` images, or the end of the list when it holds fewer
 * images. Videos are not counted, so they keep their position relative to the product images and
 * the injected images stay one uninterrupted group.
 *
 * @param {Array<Object>} items - product images with the brightcove videos already spliced in
 * @returns {number} index to inject the main set product images at
 */
const findInjectIndex = (items) => {
    let images = 0
    for (const [index, item] of items.entries()) {
        if (isBrightcoveVideo(item)) {
            continue
        }
        if (images === MAIN_SET_PRODUCT_IMAGES_PRECEDING_COUNT) {
            return index
        }
        images += 1
    }
    return items.length
}

/**
 * Builds the full gallery items list for XLEAP product gallery.
 *
 * The hook:
 * - selects the correct image group based on view type ("mainview") and selected variations
 * - includes c_extraMainImage before images from the image group
 * - appends c_extraMainImage2 after images from the image group
 * - optionally enriches a product set gallery with the main view images of its main set product
 * - inserts brightcove product videos at selected positions
 * - normalizes image assets (adds `type: 'image'` and resolves `src`)
 * - normalizes brightcove assets (maps `videoId` → `brightcoveId`)
 * - prepends the main product video (`c_main-video`) if present
 *
 * Returned items are ordered as:
 * 1. Stacked image (if exists)
 * 2. Main product video (if exists)
 * 3. The first two product images, after the set combination images have been filtered
 * 4. Up to `MAIN_SET_PRODUCT_GALLERY_IMAGES_COUNT` main set product images,
 *    when `options.enrichWithMainSetProductImages` is enabled
 * 5. The remaining product images
 *
 * The main set product images are injected last, after the brightcove videos have been spliced in,
 * so a video position counts the product set images only and the injected images stay one
 * uninterrupted group, placed after a video spliced in before the third set image.
 *
 * Differences compared to `useCockpitGalleryImages`:
 *
 * - Returns c_main-video (type: 'direct') as first and images and videos in their selected order.
 *
 *
 * @param {Object} product
 * @param {Object} product.imageGroups - Image groups from SFCC product
 * @param {Object} [product['c_main-video']] - Main product video asset
 *
 * @param {Object} derivedProduct
 * @param {Array<Object>} [derivedProduct.videos] - brightcove product videos
 * @param {Object} derivedProduct.variationParams - Selected variation attributes
 *
 * @param {Object} [stackedImage] - Stacked image entry prepended to the gallery
 *
 * @param {Object} [options]
 * @param {boolean} [options.enrichWithMainSetProductImages] - inject the main view images of the
 *  main set product after the first two product set images
 *
 * @returns {Array<Object>} Normalized gallery items ready for rendering
 *
 * @example
 * const items = useXleapGalleryImages(product, derivedProduct, stackedImage, {
 *     enrichWithMainSetProductImages: true
 * })
 */
const useXleapGalleryImages = (product, derivedProduct, stackedImage, options = {}) => {
    const {formatMessage} = useIntl()
    const {enrichWithMainSetProductImages} = options
    const mainSetProductImages = useMainSetProductImages(
        enrichWithMainSetProductImages ? product : undefined
    )

    const rawImagesAndVideos = useCockpitGalleryImages(product, derivedProduct)

    const selectionImageName = useMemo(
        () => (product?.setProducts?.length ? getProductSetSelectionImageName(product) : null),
        [product]
    )

    const filteredImagesAndVideos = useMemo(
        () =>
            selectionImageName
                ? filterSetCombinationImages(product, rawImagesAndVideos, selectionImageName)
                : rawImagesAndVideos,
        [product, rawImagesAndVideos, selectionImageName]
    )

    const imagesAndVideos = useMemo(() => {
        if (!mainSetProductImages.length) {
            return filteredImagesAndVideos
        }

        const injectIndex = findInjectIndex(filteredImagesAndVideos)

        return [
            ...filteredImagesAndVideos.slice(0, injectIndex),
            ...mainSetProductImages,
            ...filteredImagesAndVideos.slice(injectIndex)
        ]
    }, [filteredImagesAndVideos, mainSetProductImages])

    const mainVideo = product?.['c_main-video']
    const productName = product?.name

    return useMemo(() => {
        const getVideoAlt = (videoCount) =>
            formatMessage(
                {
                    id: 'product_detail.media.video_alt',
                    defaultMessage: '{productName} Video {videoCount}'
                },
                {
                    productName,
                    videoCount
                }
            )

        let videoCount = mainVideo ? 1 : 0

        return [
            stackedImage?.isLoading
                ? {
                      type: 'stacked',
                      isLoading: true
                  }
                : stackedImage?.images?.length > 0 && {
                      type: 'stacked',
                      images: stackedImage.images,
                      alt: stackedImage.alt || productName || '',
                      isLoading: stackedImage.isLoading
                  },

            mainVideo && {
                ...mainVideo,
                type: 'direct',
                videoControls: false,
                showVideoInfo: true,
                alt: getVideoAlt(1)
            },

            ...imagesAndVideos.map((iv) => {
                const normalizedItem = {
                    ...iv,
                    // default to type image
                    type: iv.type || 'image',
                    // normalize the src
                    src: iv.src || iv.disBaseLink || iv.link
                }

                if (!isBrightcoveVideo(iv)) {
                    return normalizedItem
                }

                videoCount += 1

                return {
                    ...normalizedItem,
                    videoControls: false,
                    showVideoInfo: true,
                    brightcoveId: iv.videoId,
                    poster: {
                        src: iv.poster,
                        alt: getVideoAlt(videoCount)
                    }
                }
            })
        ].filter(Boolean)
    }, [formatMessage, mainVideo, imagesAndVideos, stackedImage, productName])
}

export default useXleapGalleryImages
